import { Palette } from "./palette";

// Data-driven enemy definitions. Behaviors are interpreted by the AI system.
export type EnemyBehavior =
  | "seek_player" // classic chaser
  | "seek_node" // makes for the nearest live node (Virus)
  | "rush" // very fast, fragile (Worm)
  | "teleport" // blinks toward player, can split (Glitch)
  | "steal" // grabs fragments and flees (Packet Thief)
  | "buff" // hangs back, empowers nearby enemies (Corruptor)
  | "field" // emits an interference aura (Noise)
  | "boss"; // slow seek + telegraphed attacks (boss system)

export interface EnemyDef {
  id: string;
  name: string;
  behavior: EnemyBehavior;
  color: string;
  hp: number;
  speed: number;
  /** Damage dealt to the player or a node on contact. */
  damage: number;
  radius: number;
  /** Bandwidth (XP) granted on death. */
  xp: number;
  /** Fragments (currency) dropped on death. */
  fragments: number;
  /** Relative spawn weight in the default pool. */
  weight: number;
  /** Minimum (zone-1) this enemy starts appearing — gated by ZONE, not time. */
  minWave: number;
}

export const ENEMIES: Record<string, EnemyDef> = {
  virus: {
    id: "virus",
    name: "Virus",
    behavior: "seek_node",
    color: Palette.virus,
    hp: 18,
    speed: 58,
    damage: 8,
    radius: 12,
    xp: 3,
    fragments: 1,
    weight: 100,
    minWave: 0,
  },
  worm: {
    id: "worm",
    name: "Worm",
    behavior: "rush",
    color: Palette.worm,
    hp: 1,
    speed: 165,
    damage: 6,
    radius: 8,
    xp: 2,
    fragments: 1,
    weight: 45,
    minWave: 1,
  },
  glitch: {
    id: "glitch",
    name: "Glitch",
    behavior: "teleport",
    color: Palette.glitch,
    hp: 26,
    speed: 70,
    damage: 10,
    radius: 13,
    xp: 6,
    fragments: 2,
    weight: 30,
    minWave: 3,
  },
  thief: {
    id: "thief",
    name: "Packet Thief",
    behavior: "steal",
    color: Palette.thief,
    hp: 40,
    speed: 120,
    damage: 4,
    radius: 12,
    xp: 8,
    fragments: 5,
    weight: 14,
    minWave: 4,
  },
  noise: {
    id: "noise",
    name: "Noise",
    behavior: "field",
    color: Palette.noise,
    hp: 70,
    speed: 36,
    damage: 6,
    radius: 16,
    xp: 9,
    fragments: 2,
    weight: 16,
    minWave: 5,
  },
  corruptor: {
    id: "corruptor",
    name: "Corruptor",
    behavior: "buff",
    color: Palette.corruptor,
    hp: 90,
    speed: 44,
    damage: 2,
    radius: 15,
    xp: 14,
    fragments: 4,
    weight: 10,
    minWave: 6,
  },
};

export type EnemyId = keyof typeof ENEMIES;
